import SharePointService from "../services/sharepoint.service";
import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { Box } from '@mui/system';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CircularProgress from '@mui/material/CircularProgress';
import { useDropzone } from 'react-dropzone';

const Upload = () => {
  const [working, setWorking] = useState(false);
  const [project, setProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectData, setProjectData] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [fileUploadStatus, setFileUploadStatus] = useState({}); // Status pro Datei


  const handleWorking = (e) => { 
    setWorking(e); 
  };

  const GetSPFiles = async () => {
    handleWorking(true);
    try {
      let allFiles = await SharePointService.getUserDocuments();

      if (allFiles.message !== 'Wrong Secret') {
        const projectNames = Object.keys(allFiles.data);
        setProjects(projectNames);
        setProjectData(allFiles.data);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      handleWorking(false);
    }
  };

  useEffect(() => {
    GetSPFiles();
  }, []);

  // Setze das erste Projekt automatisch als Auswahl, sobald Projekte geladen sind
  useEffect(() => {
    if (projects.length > 0 && !project) {
      setProject(projects[0]);
    }
  }, [projects, project]);

  const handleProjectChange = (event) => {
    setProject(event.target.value);
  };

  const missingDocs = project ? projectData[project]?.missingDocs || [] : [];
  const uploadedDocs = project ? Object.values(
      projectData[project]?.files.reduce((acc, file) => {
        if (file.Art) { // Ignoriere Dateien ohne Kategorie
          acc[file.Art] = file; // Überschreibt nur, wenn die Kategorie noch nicht existiert
        }
        return acc;
      }, {})
    )
  : [];

  const DropzoneArea = ({ docName, handleFileDrop, uploadStatus, getIconForStatus, isUploaded }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: (acceptedFiles) => handleFileDrop(acceptedFiles, docName),
      accept: {
        "application/pdf": [".pdf"],
        "application/vnd.ms-excel": [".xls", ".xlsx"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        "image/jpeg": [".jpg", ".jpeg"],
        "image/png": [".png"],
      },
      multiple: true,
    });
  
    // Dynamisches Icon basierend auf `isUploaded`
    const icon = isUploaded ? (
      <CheckCircleOutlineIcon sx={{ fontSize: 40, mb: 1, color: "green" }} />
    ) : (
      <FileUploadOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
    );
  
    return (
      <Box
        {...getRootProps()}
        sx={{
          width: "100%",
          minHeight: "200px",
          textAlign: "center",
          p: 2,
          backgroundColor: isDragActive ? "action.hover" : "background.paper",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} />
        {/* Dynamisches Icon je nach Upload-Status */}
        {icon}
  
        {/* Dokumentart-Label */}
        <Typography variant="body1" sx={{ fontWeight: "bold", mt: 1 }}>
          {docName}
        </Typography>
  
        {/* Hinweistext */}
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {!isUploaded ? "Dateien hier ablegen oder klicken": "Zusätzliche Dateien hier ablegen oder klicken"}
        </Typography>
      </Box>
    );
  };
  

  const handleFileDrop = async (acceptedFiles, docName) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    setUploading(true);

    setSelectedFiles((prevFiles) => ({
      ...prevFiles,
      [docName]: [...(prevFiles[docName] || []), ...acceptedFiles],
    }));

    const initialStatus = acceptedFiles.reduce((acc, file) => {
      acc[`${docName}-${file.name}`] = "uploading";
      return acc;
    }, {});
    setFileUploadStatus((prev) => ({ ...prev, ...initialStatus }));

    const BATCH_SIZE = 3;
    for (let i = 0; i < acceptedFiles.length; i += BATCH_SIZE) {
      const batch = acceptedFiles.slice(i, i + BATCH_SIZE);
      await Promise.all(
        batch.map(async (file) => {
          const fileKey = `${docName}-${file.name}`;
          try {
            await SharePointService.uploadFiles([{ files: [file], category: docName }], project);
            setFileUploadStatus((prev) => ({ ...prev, [fileKey]: "uploaded" }));
          } catch (error) {
            console.error(`Fehler beim Hochladen von ${file.name}:`, error);
            setFileUploadStatus((prev) => ({ ...prev, [fileKey]: "error" }));
          }
        })
      );
    }
   setUploading(false);
  };

  const getIconForStatus = (status) => {
    switch (status) {
      case 'uploading':
        return <HourglassEmptyIcon sx={{ fontSize: 40, mb: 1, color: 'orange' }} />;
      case 'uploaded':
        return <CheckCircleOutlineIcon sx={{ fontSize: 40, mb: 1, color: 'green' }} />;
      default:
        return <FileUploadOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />;
    }
  };

  const allDocs = [
    ...missingDocs.map((doc) => ({ name: doc, uploaded: false })), // Fehlende Dokumente
    ...uploadedDocs.map((doc) => ({ name: doc.Art, uploaded: true })), // Hochgeladene Dokumente
  ];

  return (
    <Box sx={{ background: "#F0F2F5", py: 10 }}>
      <Container maxWidth="xl">
        <Grid container spacing={2}>
          {/* Linke Seite: Projektauswahl */}
          <Grid item xs={12} lg={3}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <h5 className="mb-3">Wählen Sie ein Projekt aus:</h5>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={project}
                label="Projekt"
                onChange={handleProjectChange}
                fullWidth // Macht das Dropdown breit
              >
                {projects.map((projName) => (
                  <MenuItem key={projName} value={projName}>
                    {projName}
                  </MenuItem>
                ))}
              </Select>
            </Paper>
          </Grid>

          {/* Rechte Seite: Fehlende Dokumente und File Picker */}
          <Grid item xs={12} lg={9}>
            <Paper elevation={1} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
              {working ? (
                <CircularProgress />
              ) : (
                <>
                  <h5 className="mb-3">Dokumente für das Projekt:</h5>
                  <Grid container spacing={2}>
                    {allDocs.map((doc, index) => (
                      <Grid item xs={12} sm={4} key={index} sx={{ display: "flex" }}>
                        <Paper
                          elevation={3}
                          sx={{
                            width: "100%",
                            height: "auto",
                            p: 3,
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <DropzoneArea
                            docName={doc.name}
                            handleFileDrop={handleFileDrop}
                            uploadStatus={uploadStatus}
                            getIconForStatus={getIconForStatus}
                            isUploaded={doc.uploaded}
                          />

                          {/* Liste der hochgeladenen Dateien */}
                          {selectedFiles[doc.name]?.length > 0 && (
                            <>
                              <Typography variant="body2" sx={{ mt: 2, fontWeight: "bold" }}>
                                Ausgewählte Dateien:
                              </Typography>
                              <Box sx={{ mt: 1, maxHeight: "100px", overflowY: "auto", overflowX: "hidden", width: "100%" }}>
                                {selectedFiles[doc.name].map((file, fileIndex) => {
                                  const fileKey = `${doc.name}-${file.name}`;
                                  const status = fileUploadStatus[fileKey]; 

                                  return (
                                    <Box
                                      key={fileIndex}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        width: "100%",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {/* Icon abhängig vom Status */}
                                      {doc.uploaded ? (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 24, color: "green", mr: 1 }} />
                                      ) : status === "uploading" ? (
                                        <HourglassEmptyIcon sx={{ fontSize: 24, color: "orange", mr: 1 }} />
                                      ) : status === "uploaded" ? (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 24, color: "green", mr: 1 }} />
                                      ) : status === "error" ? (
                                        <FileUploadOutlinedIcon sx={{ fontSize: 24, color: "red", mr: 1 }} />
                                      ) : (
                                        <FileUploadOutlinedIcon sx={{ fontSize: 24, color: "gray", mr: 1 }} />
                                      )}

                                      {/* Dateiname anzeigen */}
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }} // neu
                                      >
                                        {file.name}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </>
                          )}
                </Paper>
              </Grid>
                    ))}
</Grid>

                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Upload;
